import { useReactTable } from '@tanstack/react-table'
import { useMemo, useRef, useState } from 'react'
import type {
  DataTableCell,
  DataTableColumn,
  DataTableColumnDef,
  DataTableColumnFilterFnsState,
  DataTableColumnOrderState,
  DataTableColumnSizingInfoState,
  DataTableDefinedTableOptions,
  DataTableFilterOption,
  DataTableGroupingState,
  DataTableInstance,
  DataTablePaginationState,
  DataTableRow,
  DataTableRowData,
  DataTableState,
  DataTableStatefulTableOptions,
  DataTableUpdater,
} from '../types'
import { getAllLeafColumnDefs, getColumnId, getDefaultColumnFilterFn } from '../utils/column.utils'
import { createRow } from '../utils/tanstack.helpers'

import { useTableEffects } from './use-table-effects'

/**
 * The MRT hook that wraps the TanStack useReactTable hook and adds additional functionality
 * @param definedTableOptions - table options with proper defaults set
 * @returns the MRT table instance
 */
export const useTableTableInstance = <TData extends DataTableRowData>(
  definedTableOptions: DataTableDefinedTableOptions<TData>,
): DataTableInstance<TData> => {
  const lastSelectedRowId = useRef<null | string>(null)
  const bottomToolbarRef = useRef<HTMLDivElement>(null)
  const editInputRefs = useRef<Record<string, HTMLInputElement>>({})
  const filterInputRefs = useRef<Record<string, HTMLInputElement>>({})
  const searchInputRef = useRef<HTMLInputElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const tableHeadCellRefs = useRef<Record<string, HTMLTableCellElement>>({})
  const tablePaperRef = useRef<HTMLDivElement>(null)
  const topToolbarRef = useRef<HTMLDivElement>(null)
  const tableHeadRef = useRef<HTMLTableSectionElement>(null)
  const tableFooterRef = useRef<HTMLTableSectionElement>(null)

  // transform initial state with proper column order
  const initialState: Partial<DataTableState<TData>> = useMemo(() => {
    const initState = definedTableOptions.initialState ?? {}
    initState.globalFilterFn = definedTableOptions.globalFilterFn ?? 'fuzzy'
    return initState
  }, [])

  definedTableOptions.initialState = initialState

  const [creatingRow, setCreatingRowLocal] = useState<DataTableRow<TData> | null>(
    initialState.creatingRow ?? null,
  )
  const [columnFilterFns, setColumnFilterFns] = useState<DataTableColumnFilterFnsState>(() =>
    Object.assign(
      {},
      ...getAllLeafColumnDefs(definedTableOptions.columns as DataTableColumnDef<TData>[]).map(
        (col) => ({
          [getColumnId(col)]:
            col.filterFn instanceof Function
              ? (col.filterFn.name ?? 'custom')
              : (col.filterFn ??
                initialState?.columnFilterFns?.[getColumnId(col)] ??
                getDefaultColumnFilterFn()),
        }),
      ),
    ),
  )
  const [columnOrder, onColumnOrderChange] = useState<DataTableColumnOrderState>(
    initialState.columnOrder ?? [],
  )
  const [columnSizingInfo, onColumnSizingInfoChange] = useState<DataTableColumnSizingInfoState>(
    initialState.columnSizingInfo ?? ({} as DataTableColumnSizingInfoState),
  )

  const [draggingColumn, setDraggingColumn] = useState<DataTableColumn<TData> | null>(
    initialState.draggingColumn ?? null,
  )
  const [draggingRow, setDraggingRow] = useState<DataTableRow<TData> | null>(
    initialState.draggingRow ?? null,
  )
  const [editingCell, setEditingCell] = useState<DataTableCell<TData> | null>(
    initialState.editingCell ?? null,
  )
  const [editingRow, setEditingRow] = useState<DataTableRow<TData> | null>(
    initialState.editingRow ?? null,
  )
  const [globalFilterFn, setGlobalFilterFn] = useState<DataTableFilterOption>(
    initialState.globalFilterFn ?? 'fuzzy',
  )
  const [grouping, onGroupingChange] = useState<DataTableGroupingState>(initialState.grouping ?? [])
  const [hoveredColumn, setHoveredColumn] = useState<Partial<DataTableColumn<TData>> | null>(
    initialState.hoveredColumn ?? null,
  )
  const [hoveredRow, setHoveredRow] = useState<Partial<DataTableRow<TData>> | null>(
    initialState.hoveredRow ?? null,
  )

  const [pagination, onPaginationChange] = useState<DataTablePaginationState>(
    initialState?.pagination ?? { pageIndex: 0, pageSize: 10 },
  )
  const [showAlertBanner, setShowAlertBanner] = useState<boolean>(
    initialState?.showAlertBanner ?? false,
  )
  const [showColumnFilters, setShowColumnFilters] = useState<boolean>(
    initialState?.showColumnFilters ?? false,
  )
  const [showGlobalFilter, setShowGlobalFilter] = useState<boolean>(
    initialState?.showGlobalFilter ?? false,
  )
  const [showToolbarDropZone, setShowToolbarDropZone] = useState<boolean>(
    initialState?.showToolbarDropZone ?? false,
  )

  const [isAllRowsSelected, setSelectAllRecords] = useState<boolean>(
    initialState?.isAllRowsSelected ?? false,
  )

  definedTableOptions.state = {
    columnFilterFns,
    columnOrder,
    columnSizingInfo,
    creatingRow,
    draggingColumn,
    draggingRow,
    editingCell,
    editingRow,
    globalFilterFn,
    grouping,
    hoveredColumn,
    hoveredRow,
    pagination,
    showAlertBanner,
    showColumnFilters,
    showGlobalFilter,
    showToolbarDropZone,
    isAllRowsSelected,
    ...definedTableOptions.state,
  }

  // The table options now include all state needed to help determine column visibility and order logic
  const statefulTableOptions = definedTableOptions as DataTableStatefulTableOptions<TData>

  // don't recompute columnDefs while resizing column or dragging column/row
  const columnDefsRef = useRef<DataTableColumnDef<TData>[]>([])
  columnDefsRef.current = statefulTableOptions.columns

  // @ts-expect-error Datatable
  const table = useReactTable({
    onColumnOrderChange,
    onColumnSizingInfoChange,
    onGroupingChange,
    onPaginationChange,
    ...statefulTableOptions,
    globalFilterFn: statefulTableOptions.filterFns?.[globalFilterFn ?? 'fuzzy'],
  }) as DataTableInstance<TData>

  table.refs = {
    bottomToolbarRef,
    editInputRefs,
    filterInputRefs,
    lastSelectedRowId,
    searchInputRef,
    tableContainerRef,
    tableFooterRef,
    tableHeadCellRefs,
    tableHeadRef,
    tablePaperRef,
    topToolbarRef,
  }

  table.setCreatingRow = (row: DataTableUpdater<DataTableRow<TData> | null | true>) => {
    let _row = row
    if (row === true) {
      _row = createRow(table)
    }
    statefulTableOptions?.onCreatingRowChange?.(_row as DataTableRow<TData> | null) ??
      setCreatingRowLocal(_row as DataTableRow<TData> | null)
  }
  table.setColumnFilterFns = statefulTableOptions.onColumnFilterFnsChange ?? setColumnFilterFns

  table.setDraggingColumn = statefulTableOptions.onDraggingColumnChange ?? setDraggingColumn
  table.setDraggingRow = statefulTableOptions.onDraggingRowChange ?? setDraggingRow
  table.setEditingCell = statefulTableOptions.onEditingCellChange ?? setEditingCell
  table.setEditingRow = statefulTableOptions.onEditingRowChange ?? setEditingRow
  table.setGlobalFilterFn = statefulTableOptions.onGlobalFilterFnChange ?? setGlobalFilterFn
  table.setHoveredColumn = statefulTableOptions.onHoveredColumnChange ?? setHoveredColumn
  table.setHoveredRow = statefulTableOptions.onHoveredRowChange ?? setHoveredRow

  table.setShowAlertBanner = statefulTableOptions.onShowAlertBannerChange ?? setShowAlertBanner
  table.setShowColumnFilters =
    statefulTableOptions.onShowColumnFiltersChange ?? setShowColumnFilters
  table.setShowGlobalFilter = statefulTableOptions.onShowGlobalFilterChange ?? setShowGlobalFilter
  table.setShowToolbarDropZone =
    statefulTableOptions.onShowToolbarDropZoneChange ?? setShowToolbarDropZone
  table.setSelectAllRecords = statefulTableOptions.onSelectAllRecords ?? setSelectAllRecords
  useTableEffects(table)

  return table
}
